import React, { useReducer, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminContext from './adminContext';
import adminReducer from './adminReducer';
import {
  ERROR_STATES,
  FORM_SUBMISSION,
  SET_SUBMITTED_FORMS,
  SET_PAGINATED_FORMS,
  SET_FORM_LOADING,
  SET_RECENT_FORMS,
  GET_PORTAL_USERS,
  PORTAL_USER_LOADING,
  SET_ACTIVE_SESSIONS,
  SET_SESSION_STATS,
} from './actionTypes';
import { handleApiError, logError } from '../../Components/Utils/errorHandler';
import { isSuccessResponse } from '../../Components/Utils/apiHelpers';
import authService from '../../Services/authService';
import {
  fetchAllForms,
  fetchFormsByUser,
  fetchFormsPaginated,
  fetchFormsPaginatedByUser,
  fetchRecentForms,
  fetchAllUsers,
  fetchUserByUsername,
  deleteUser,
  assignUserAuthorities,
  replaceUserAuthorities,
  fetchAllStores,
  deleteStore as deleteStoreApi,
  fetchAllSessions,
  revokeSession as revokeSessionApi,
  cleanupExpiredTokens as cleanupExpiredTokensApi,
  calculateSessionStats as calcSessionStats,
  normalizeAuthorities,
  submitForm,
} from '../../Services/adminApiService';

/**
 * Admin State Provider
 * Manages all admin-related state including users, forms, sessions, and security
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
const AdminState = (props) => {
  const navigation = useNavigate();

  /** @type {import('./types').AdminState} */
  const initialState = {
    portalUser: null,
    portalUserList: null,
    submittedForms: null,
    paginatedForms: null,
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 20,
    hasNext: false,
    hasPrevious: false,
    formLoading: false,
    submitSuccess: false,
    submitFailed: false,
    successMessage: null,
    errorMessage: null,
    errorCode: null,
    submitStatus: false,
    auth: authService.getToken(),
    // Session Management
    activeSessions: null,
    sessionStats: null,
    sessionLoading: false,
    // Security
    securityMetrics: null,
    securityLoading: false,
  };

  const [state, dispatch] = useReducer(adminReducer, initialState);

  const clearAdminErrors = useCallback(() => {
    dispatch({
      type: ERROR_STATES,
      errorMessage: null,
      userFriendlyMessage: null,
      errorReference: null,
      errorCode: null,
      submitFailed: false,
      submitStatus: false,
    });
  }, []);

  const getSubmittedFormsByUser = useCallback(
    async (id) => {
      if (!id) {
        logError('getSubmittedFormsByUser', new Error('User ID is required'));
        return;
      }

      dispatch({ type: SET_FORM_LOADING, payload: true });
      try {
        const data = await fetchFormsByUser(id);
        if (data) {
          dispatch({ type: SET_SUBMITTED_FORMS, payload: data });
        }
        dispatch({ type: SET_FORM_LOADING, payload: false });
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'getSubmittedFormsByUser'
        );
        dispatch({ type: SET_FORM_LOADING, payload: false });
      }
    },
    [navigation]
  );

  const getSubmittedForms = useCallback(async () => {
    dispatch({ type: SET_FORM_LOADING, payload: true });
    try {
      const data = await fetchAllForms();
      if (data) {
        dispatch({ type: SET_SUBMITTED_FORMS, payload: data });
      }
      dispatch({ type: SET_FORM_LOADING, payload: false });
    } catch (error) {
      handleApiError(
        error,
        dispatch,
        ERROR_STATES,
        navigation,
        'getSubmittedForms'
      );
      dispatch({ type: SET_FORM_LOADING, payload: false });
    }
  }, [navigation]);

  const getRecentForms = useCallback(
    async (size = 10) => {
      try {
        const recentSlice = await fetchRecentForms(size);
        dispatch({ type: SET_RECENT_FORMS, payload: recentSlice });
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'getRecentForms'
        );
      }
    },
    [navigation]
  );

  const getFormsPaginated = useCallback(
    async (params = {}) => {
      dispatch({ type: SET_FORM_LOADING, payload: true });
      try {
        const data = await fetchFormsPaginated(params);
        if (data) {
          dispatch({
            type: SET_PAGINATED_FORMS,
            payload: {
              data,
              requestedPage: params.page,
              currentPage: data.currentPage,
              totalPages: data.totalPages,
              totalElements: data.totalElements,
              pageSize: data.pageSize,
              hasNext: data.hasNext,
              hasPrevious: data.hasPrevious,
            },
          });
        }
        dispatch({ type: SET_FORM_LOADING, payload: false });
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'getFormsPaginated'
        );
        dispatch({ type: SET_FORM_LOADING, payload: false });
      }
    },
    [navigation]
  );

  // Server-side pagination filtered by portal user
  const getFormsPaginatedByPortalUser = useCallback(
    async (portalUserId, params = {}) => {
      if (!portalUserId) {
        logError(
          'getFormsPaginatedByPortalUser',
          new Error('portalUserId is required')
        );
        return;
      }

      dispatch({ type: SET_FORM_LOADING, payload: true });
      try {
        const data = await fetchFormsPaginatedByUser(portalUserId, params);
        if (data) {
          dispatch({
            type: SET_PAGINATED_FORMS,
            payload: {
              data,
              requestedPage: params.page,
              currentPage: data.currentPage,
              totalPages: data.totalPages,
              totalElements: data.totalElements,
              pageSize: data.pageSize,
              hasNext: data.hasNext,
              hasPrevious: data.hasPrevious,
            },
          });
        }
        dispatch({ type: SET_FORM_LOADING, payload: false });
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'getFormsPaginatedByPortalUser'
        );
        dispatch({ type: SET_FORM_LOADING, payload: false });
      }
    },
    [navigation]
  );

  const formUpdate = useCallback(
    async (body, form, department) => {
      if (!body || !form || !department) {
        logError('formUpdate', new Error('Missing required parameters'));
        dispatch({
          type: ERROR_STATES,
          errorMessage: 'Invalid form update request',
          submitFailed: true,
          formLoading: false,
        });
        return;
      }

      dispatch({ type: SET_FORM_LOADING, payload: true });
      try {
        const submittedForm = await submitForm(body, form, department);

        if (isSuccessResponse(submittedForm)) {
          dispatch({
            type: FORM_SUBMISSION,
            successMessage: 'Your form has been updated successfully!',
            submitStatus: true,
            submitSuccess: true,
            formLoading: false,
          });
        } else {
          dispatch({
            type: ERROR_STATES,
            payload: submittedForm.data,
            submitFailed: true,
            submitStatus: false,
            errorMessage:
              submittedForm.data?.apiError?.errorMessage ||
              'Form update failed',
            errorCode: submittedForm.status,
            success: false,
            formLoading: false,
          });
        }
      } catch (err) {
        handleApiError(err, dispatch, ERROR_STATES, navigation, 'formUpdate');
        dispatch({
          type: ERROR_STATES,
          submitFailed: true,
          submitStatus: true,
          submittedData: err.config?.data,
          success: false,
        });
      }
    },
    [navigation]
  );

  const getAllUsers = useCallback(async () => {
    dispatch({ type: PORTAL_USER_LOADING, payload: true });
    try {
      const data = await fetchAllUsers();
      if (data) {
        dispatch({ type: GET_PORTAL_USERS, payload: data });
      }
      dispatch({ type: PORTAL_USER_LOADING, payload: false });
    } catch (error) {
      handleApiError(error, dispatch, ERROR_STATES, navigation, 'getAllUsers');
      dispatch({ type: PORTAL_USER_LOADING, payload: false });
    }
  }, [navigation]);

  const getPortalUser = useCallback(
    async (userName) => {
      if (!userName) {
        logError('getPortalUser', new Error('Username is required'));
        return null;
      }

      try {
        return await fetchUserByUsername(userName);
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'getPortalUser'
        );
        return null;
      }
    },
    [navigation]
  );

  const deletePortalUser = useCallback(
    async (userId) => {
      if (!userId) {
        logError('deletePortalUser', new Error('User ID is required'));
        return { success: false, error: 'User ID is required' };
      }

      try {
        const data = await deleteUser(userId);
        // Refresh the user list after deletion
        await getAllUsers();
        return { success: true, data };
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'deletePortalUser'
        );
        return {
          success: false,
          error: error.response?.data?.message || 'Failed to delete user',
        };
      }
    },
    [navigation, getAllUsers]
  );

  const deleteStore = useCallback(async (storeId) => {
    if (!storeId) {
      logError('deleteStore', new Error('Store ID is required'));
      return { success: false, error: 'Store ID is required' };
    }

    try {
      const data = await deleteStoreApi(storeId);
      return { success: true, data };
    } catch (error) {
      logError('deleteStore', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete store',
      };
    }
  }, []);

  const getAllStores = useCallback(async () => {
    try {
      return await fetchAllStores();
    } catch (error) {
      logError('getAllStores', error);
      return [];
    }
  }, []);

  const setFormLoading = useCallback((loading) => {
    dispatch({ type: SET_FORM_LOADING, payload: loading });
  }, []);

  const setUserLoading = useCallback((loading) => {
    dispatch({ type: PORTAL_USER_LOADING, payload: loading });
  }, []);

  const calculateSessionStats = useCallback((sessions) => {
    const stats = calcSessionStats(sessions);
    dispatch({ type: SET_SESSION_STATS, payload: stats });
  }, []);

  // Session Management Functions
  const getAllSessions = useCallback(async () => {
    try {
      const data = await fetchAllSessions();
      if (data) {
        dispatch({ type: SET_ACTIVE_SESSIONS, payload: data });
        calculateSessionStats(data);
      }
    } catch (error) {
      console.error('Failed to get sessions:', error);
    }
  }, [calculateSessionStats]);

  const revokeSession = useCallback(
    async (sessionId) => {
      try {
        await revokeSessionApi(sessionId);
        getAllSessions(); // Refresh list
        return { success: true };
      } catch (error) {
        console.error('Failed to revoke session:', error);
        return { success: false };
      }
    },
    [getAllSessions]
  );

  const cleanupExpiredTokens = useCallback(async () => {
    try {
      const data = await cleanupExpiredTokensApi();
      getAllSessions();
      return { success: true, deletedCount: data?.deletedCount || 0 };
    } catch (error) {
      console.error('Failed to cleanup tokens:', error);
      return { success: false };
    }
  }, [getAllSessions]);

  // Authority Management
  const assignAuthorities = useCallback(
    async (userId, authorities) => {
      const roles = normalizeAuthorities(authorities);
      if (!userId) {
        logError('assignAuthorities', new Error('User ID is required'));
        return { success: false, error: 'User ID is required' };
      }
      if (roles.length === 0) {
        return {
          success: false,
          error: 'At least one valid authority is required',
        };
      }
      try {
        const data = await assignUserAuthorities(userId, roles);
        await getAllUsers();
        return { success: true, data };
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'assignAuthorities'
        );
        return {
          success: false,
          error:
            error.response?.data?.message || 'Failed to assign authorities',
        };
      }
    },
    [navigation, getAllUsers]
  );

  const replaceAuthorities = useCallback(
    async (userId, authorities) => {
      const roles = normalizeAuthorities(authorities);
      if (!userId) {
        logError('replaceAuthorities', new Error('User ID is required'));
        return { success: false, error: 'User ID is required' };
      }
      if (roles.length === 0) {
        return {
          success: false,
          error: 'At least one valid authority is required',
        };
      }
      try {
        const data = await replaceUserAuthorities(userId, roles);
        await getAllUsers();
        return { success: true, data };
      } catch (error) {
        handleApiError(
          error,
          dispatch,
          ERROR_STATES,
          navigation,
          'replaceAuthorities'
        );
        return {
          success: false,
          error:
            error.response?.data?.message || 'Failed to replace authorities',
        };
      }
    },
    [navigation, getAllUsers]
  );

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      submittedForms: state.submittedForms,
      paginatedForms: state.paginatedForms,
      currentPage: state.currentPage,
      totalPages: state.totalPages,
      totalElements: state.totalElements,
      pageSize: state.pageSize,
      hasNext: state.hasNext,
      hasPrevious: state.hasPrevious,
      portalUser: state.portalUser,
      portalUserList: state.portalUserList,
      formLoading: state.formLoading,
      submitSuccess: state.submitSuccess,
      submitFailed: state.submitFailed,
      successMessage: state.successMessage,
      errorMessage: state.errorMessage,
      userFriendlyMessage: state.userFriendlyMessage,
      errorReference: state.errorReference,
      errorCode: state.errorCode,
      submitStatus: state.submitStatus,
      activeSessions: state.activeSessions,
      sessionStats: state.sessionStats,
      sessionLoading: state.sessionLoading,
      securityMetrics: state.securityMetrics,
      securityLoading: state.securityLoading,
      formUpdate,
      getSubmittedForms,
      getFormsPaginated,
      getSubmittedFormsByUser,
      setFormLoading,
      getAllUsers,
      getPortalUser,
      deletePortalUser,
      deleteStore,
      getAllStores,
      setUserLoading,
      getAllSessions,
      revokeSession,
      cleanupExpiredTokens,
      clearAdminErrors,
      getRecentForms,
      recentForms: state.recentForms,
      assignAuthorities,
      replaceAuthorities,
      getFormsPaginatedByPortalUser,
    }),
    [
      state,
      formUpdate,
      getSubmittedForms,
      getFormsPaginated,
      getSubmittedFormsByUser,
      setFormLoading,
      getAllUsers,
      getPortalUser,
      deletePortalUser,
      deleteStore,
      getAllStores,
      setUserLoading,
      getAllSessions,
      revokeSession,
      cleanupExpiredTokens,
      clearAdminErrors,
      getRecentForms,
      assignAuthorities,
      replaceAuthorities,
      getFormsPaginatedByPortalUser,
    ]
  );

  return (
    <AdminContext.Provider value={contextValue}>
      {props.children}
    </AdminContext.Provider>
  );
};

export default AdminState;
